import { callAIEndpoint, parseJsonErrorBody } from './aiClient';
import { throwFromApiErrorResponse } from './creditErrors';
import { applyServerBalance } from '@/lib/client/applyServerBalance';
import {
  logClientBillingDiagnostics,
  resolveClientRequestId,
} from './billingTrace';

const ENDPOINT = '/api/ai/chat-completion';
const DREAMDNA_BATCH_ENDPOINT = '/api/ai/dreamdna-batch';

export interface ChatCompletionMeta {
  balance?: number;
  creditCost?: number;
  creditsCharged?: boolean;
  creditsRestored?: boolean;
  idempotentReplay?: boolean;
  requestId?: string;
  billingPath?: 'ledger' | 'legacy';
  billingOutcome?: string;
}

export interface ChatCompletionResult extends ChatCompletionMeta {
  choices?: Array<{
    message?: { content?: string };
    delta?: { content?: string };
  }>;
  [key: string]: unknown;
}

export interface ChatCompletionRequestOptions {
  idempotencyKey?: string;
  feature?: string;
  operationKey?: string;
  caller?: string;
  requestId?: string;
  expectJson?: boolean;
  signal?: AbortSignal;
}

export interface DreamDnaBatchResult {
  sections: Record<string, unknown>;
  missingSections?: string[];
  balance?: number;
  creditCost?: number;
  creditsCharged?: boolean;
  creditsRestored?: boolean;
  idempotentReplay?: boolean;
  requestId?: string;
  billingPath?: string;
  billingOutcome?: string;
  error?: string;
}

/**
 * Calls the DreamDNA batch endpoint to generate all 16 sections in a single
 * AI request. Costs 1 credit for the entire analysis.
 */
export async function getDreamDnaBatch(args: {
  systemPrompt: string;
  userPrompt: string;
  idempotencyKey: string;
  signal?: AbortSignal;
}): Promise<DreamDnaBatchResult> {
  const requestId = resolveClientRequestId(undefined);

  const response = await fetch(DREAMDNA_BATCH_ENDPOINT, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'Idempotency-Key': args.idempotencyKey,
      'X-AIMaker-Request-ID': requestId,
    },
    body: JSON.stringify({
      systemPrompt: args.systemPrompt,
      userPrompt: args.userPrompt,
      idempotencyKey: args.idempotencyKey,
      requestId,
      caller: 'dream-workspace:dreamdna-batch',
      feature: 'dreamdna',
      operationKey: 'dreamdna-batch',
    }),
    signal: args.signal,
  });

  const data = (await parseJsonErrorBody(response)) as Record<string, unknown>;

  if (!response.ok) {
    throwFromApiErrorResponse(response.status, data);
  }

  if (typeof data.balance === 'number') {
    applyServerBalance(data.balance);
  }

  logClientBillingDiagnostics({
    requestId: typeof data.requestId === 'string' ? data.requestId : requestId,
    billingPath: data.billingPath as 'ledger' | 'legacy' | undefined,
    billingOutcome: typeof data.billingOutcome === 'string' ? data.billingOutcome : undefined,
    balance: typeof data.balance === 'number' ? data.balance : undefined,
    idempotentReplay: data.idempotentReplay === true,
    creditCost: typeof data.creditCost === 'number' ? data.creditCost : undefined,
  });

  return {
    sections: (data.sections as Record<string, unknown>) ?? {},
    missingSections: Array.isArray(data.missingSections)
      ? (data.missingSections as string[])
      : undefined,
    balance: typeof data.balance === 'number' ? data.balance : undefined,
    creditCost: typeof data.creditCost === 'number' ? data.creditCost : undefined,
    creditsCharged: data.creditsCharged === true,
    creditsRestored: data.creditsRestored === true,
    idempotentReplay: data.idempotentReplay === true,
    requestId: typeof data.requestId === 'string' ? data.requestId : requestId,
    billingPath: typeof data.billingPath === 'string' ? data.billingPath : undefined,
    billingOutcome: typeof data.billingOutcome === 'string' ? data.billingOutcome : undefined,
    error: typeof data.error === 'string' ? data.error : undefined,
  };
}

function logSafeDiagnostics(result: ChatCompletionResult, requestId: string): void {
  logClientBillingDiagnostics({
    requestId: typeof result.requestId === 'string' ? result.requestId : requestId,
    billingPath: result.billingPath,
    billingOutcome: result.billingOutcome,
    balance: typeof result.balance === 'number' ? result.balance : undefined,
    idempotentReplay: result.idempotentReplay === true,
    creditCost: typeof result.creditCost === 'number' ? result.creditCost : undefined,
  });
}

export async function getChatCompletion(
  provider: string,
  model: string,
  messages: object[],
  parameters: object = {},
  options: ChatCompletionRequestOptions = {}
): Promise<ChatCompletionResult> {
  const requestId = resolveClientRequestId(options.requestId);
  const caller = options.caller?.trim() || 'unknown-legacy-caller';

  const result = (await callAIEndpoint(
    ENDPOINT,
    {
      provider,
      model,
      messages,
      stream: false,
      parameters,
      requestId,
      caller,
      ...(options.feature ? { feature: options.feature } : {}),
      ...(options.operationKey ? { operationKey: options.operationKey } : {}),
      ...(options.expectJson ? { expectJson: true } : {}),
      ...(options.idempotencyKey ? { idempotencyKey: options.idempotencyKey } : {}),
    },
    {
      idempotencyKey: options.idempotencyKey,
      requestId,
      signal: options.signal,
    }
  )) as ChatCompletionResult;

  // Apply once here so non-stream callers get the same balance UX as streaming.
  applyServerBalance(result.balance);
  logSafeDiagnostics(result, requestId);

  return result;
}

export async function refundGenerationAttempt(args: {
  idempotencyKey: string;
  errorCode?: string;
}): Promise<{
  outcome?: string;
  balance?: number;
  creditsRestored?: boolean;
  responseText?: string | null;
}> {
  const response = await fetch('/api/ai/generation-attempt/refund', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'Idempotency-Key': args.idempotencyKey,
    },
    body: JSON.stringify({
      idempotencyKey: args.idempotencyKey,
      errorCode: args.errorCode ?? 'cancelled',
    }),
  });

  const data = await parseJsonErrorBody(response);
  if (!response.ok && response.status !== 404) {
    throwFromApiErrorResponse(response.status, data);
  }

  if (typeof data.balance === 'number') {
    applyServerBalance(data.balance);
  }

  return {
    outcome: typeof data.outcome === 'string' ? data.outcome : undefined,
    balance: typeof data.balance === 'number' ? data.balance : undefined,
    creditsRestored: data.creditsRestored === true,
    responseText: typeof data.responseText === 'string' ? data.responseText : null,
  };
}

// NOTE: clearStaleGenerationLocks has been removed.
// The stale-lock infrastructure was designed for the old 16-sequential-request architecture.
// Under the new single-batch architecture, there is only one idempotency key per DreamDNA
// analysis, so stale per-section locks cannot accumulate. The endpoint now returns 410 Gone.

export async function getStreamingChatCompletion(
  provider: string,
  model: string,
  messages: object[],
  onChunk: (chunk: unknown) => void,
  onComplete: (meta?: ChatCompletionMeta) => void,
  onError: (error: Error) => void,
  parameters: object = {},
  options: Pick<ChatCompletionRequestOptions, 'caller' | 'requestId' | 'feature' | 'operationKey'> = {}
) {
  const requestId = resolveClientRequestId(options.requestId);
  const caller = options.caller?.trim() || 'unknown-legacy-caller';

  try {
    const response = await fetch(ENDPOINT, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-AIMaker-Request-ID': requestId,
      },
      body: JSON.stringify({
        provider,
        model,
        messages,
        stream: true,
        parameters,
        requestId,
        caller,
        ...(options.feature ? { feature: options.feature } : {}),
        ...(options.operationKey ? { operationKey: options.operationKey } : {}),
      }),
    });

    if (!response.ok) {
      const data = await parseJsonErrorBody(response);
      throwFromApiErrorResponse(response.status, data);
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error('Response body is not readable');

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;

        try {
          const data = JSON.parse(line.slice(6));
          if (data.type === 'chunk' && data.chunk) {
            onChunk(data.chunk);
          } else if (data.type === 'done') {
            const meta: ChatCompletionMeta = {
              balance: data.balance,
              creditCost: data.creditCost,
              creditsCharged: data.creditsCharged,
              creditsRestored: data.creditsRestored,
              idempotentReplay: data.idempotentReplay,
              requestId: data.requestId ?? requestId,
              billingPath: data.billingPath,
              billingOutcome: data.billingOutcome,
            };
            applyServerBalance(data.balance);
            logSafeDiagnostics(meta as ChatCompletionResult, requestId);
            onComplete(meta);
          } else if (data.type === 'error') {
            console.error('API Route Error:', { error: data.error });
            if (typeof data.balance === 'number') {
              applyServerBalance(data.balance);
            }
            onError(new Error(data.error || 'Generation failed'));
          }
        } catch {
          // Skip invalid JSON
        }
      }
    }
  } catch (error) {
    console.error('Streaming error:', error);
    onError(error instanceof Error ? error : new Error('Streaming error'));
  }
}
